<template>
  <div class="ticket-scanner">
    <video ref="video" class="ticket-scanner__video" playsinline muted autoplay></video>

    <!-- Só existe no caminho do jsQR: é nele que o quadro é reduzido antes de
         decodificar. Fica fora da tela, nunca é mostrado. -->
    <canvas ref="canvas" class="ticket-scanner__canvas"></canvas>

    <!-- Moldura: mira para o operador, sem recortar nada do quadro analisado. -->
    <div class="ticket-scanner__frame"></div>

    <div v-if="paused" class="ticket-scanner__paused">
      <i class="feather icon-pause-circle"></i>
    </div>
  </div>
</template>

<script>
/**
 * Leitura de QR pela câmera, para a portaria.
 *
 * Dois decodificadores, escolhidos em tempo de execução:
 *
 * 1. **`BarcodeDetector` nativa** — decodificação em código nativo, sem baixar
 *    nada e sem ocupar a thread principal com processamento de imagem. Cobre
 *    Chrome e Edge, no Android e no desktop. É o caminho rápido.
 * 2. **jsQR**, carregado por `import()` dinâmico — **Safari e iOS não implementam
 *    a API nativa**, e sem esta segunda via um validador de iPhone ficaria só com
 *    a digitação. Custa ~10 KB e roda em JS puro, então o quadro é reduzido antes
 *    de decodificar (ver `scanWithJsQr`) e a cadência cai pela metade.
 *
 * O chunk sai do `<link rel="prefetch">` no `vue.config.js`: quem nunca abre a
 * portaria não baixa decodificador nenhum.
 *
 * O componente não valida nada — emite o texto lido e sai da frente.
 */
export default {
  name: 'TicketScanner',
  props: {
    /** Congela a leitura enquanto a tela mostra o resultado da anterior. */
    paused: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      stream: null,
      detector: null,
      jsQR: null,
      timer: null,
      lastValue: '',
      lastAt: 0
    }
  },
  mounted() {
    this.start()
  },
  beforeDestroy() {
    // Sem isto a luz da câmera fica acesa depois de sair da tela — e o navegador
    // segura o dispositivo, impedindo outra aba de usá-lo.
    this.stop()
  },
  methods: {
    hasNativeDetector() {
      return typeof window !== 'undefined' && 'BarcodeDetector' in window
    },

    async start() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$emit('unsupported', 'no-camera-api')
        return
      }

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          // `ideal` e não `exact`: em notebook só existe a câmera frontal, e
          // `exact` falharia em vez de usar o que há.
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        })

        const video = this.$refs.video
        if (!video) return

        video.srcObject = this.stream
        await video.play()

        if (this.hasNativeDetector()) {
          this.detector = new window.BarcodeDetector({ formats: ['qr_code'] })
          // ~8 leituras por segundo: acompanha a mão de quem aproxima o celular
          // e deixa folga de CPU num aparelho de portaria, que raramente é bom.
          this.timer = setInterval(this.scanOnce, 120)
          this.$emit('ready', 'native')
          return
        }

        const module = await import(/* webpackChunkName: "ticket-scanner" */ 'jsqr')
        this.jsQR = module.default || module
        // Metade da cadência: aqui cada quadro é desenhado, lido pixel a pixel e
        // decodificado em JS. Mais rápido que isso só rouba CPU sem ler melhor.
        this.timer = setInterval(this.scanOnce, 240)
        this.$emit('ready', 'js')
      } catch (error) {
        this.stop()
        this.$emit('denied', error && error.name ? error.name : 'error')
      }
    },

    stop() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }

      this.detector = null
      this.jsQR = null
    },

    async scanOnce() {
      if (this.paused) return

      const video = this.$refs.video
      // HAVE_CURRENT_DATA: antes disso não há quadro para analisar.
      if (!video || video.readyState < 2) return

      try {
        const value = this.detector
          ? await this.scanWithDetector(video)
          : this.scanWithJsQr(video)

        if (!value) return

        // O mesmo código fica no quadro por vários ciclos enquanto a pessoa
        // segura o celular. Sem esta guarda a tela dispararia a mesma validação
        // dezenas de vezes por segundo.
        const now = Date.now()
        if (value === this.lastValue && now - this.lastAt < 2500) return

        this.lastValue = value
        this.lastAt = now
        this.$emit('detected', value)
      } catch (error) {
        // Quadro tremido ou fora de foco faz o decodificador lançar. É rotina.
      }
    },

    async scanWithDetector(video) {
      const codes = await this.detector.detect(video)

      return codes && codes.length ? String(codes[0].rawValue || '').trim() : ''
    },

    /**
     * O quadro é reduzido para 480 px de largura antes de decodificar.
     *
     * jsQR percorre a imagem inteira em JS: num quadro de 1280×720 são quase um
     * milhão de pixels por leitura, e o scanner engasgaria justamente no aparelho
     * fraco que costuma ficar na portaria. Um QR ocupa bem mais que os poucos
     * pixels que 480 px de largura dão de resolução — reduzir não custa leitura,
     * custa só trabalho que não precisava ser feito.
     */
    scanWithJsQr(video) {
      const canvas = this.$refs.canvas
      if (!canvas || !this.jsQR || !video.videoWidth) return ''

      const width = 480
      const height = Math.round((video.videoHeight / video.videoWidth) * width) || 360

      canvas.width = width
      canvas.height = height

      const context = canvas.getContext('2d', { willReadFrequently: true })
      context.drawImage(video, 0, 0, width, height)

      const image = context.getImageData(0, 0, width, height)
      // `dontInvert`: ingresso é escuro sobre claro. Tentar o negativo dobraria o
      // custo de cada quadro para cobrir um caso que não existe aqui.
      const found = this.jsQR(image.data, width, height, { inversionAttempts: 'dontInvert' })

      return found && found.data ? String(found.data).trim() : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.ticket-scanner {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 0.6rem;
  background: #0b0b0b;
}

.ticket-scanner__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ticket-scanner__canvas {
  display: none;
}

.ticket-scanner__frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 62%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 0.9rem;
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.35);
  pointer-events: none;
}

.ticket-scanner__paused {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 2.5rem;
}
</style>
